import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Polygon } from "react-leaflet";
import axios from "../../../../services/axios";
import Constants from "../../../../constants/Index";
import { ICoords } from "../../../../@types/GoogleMaps/ICoord";
import { useEditOnboarding } from "../../../../hooks/useEditOnboarding";
import { useFilter } from "../../../../hooks/useFilter";
import { AlertModal } from "../../../Modal/Alert/Index";
import { Modal } from "../../../Modal/Default/Index";
import { ButtonRedirect } from "../../../Modal/Error/ButtonRedirect/Index";
import { ButtonOnlyTitle } from "../../../Buttons/ButtonOnlyTitle/Index";
import { RoundedCloseButton } from "../../../Buttons/RoundedCloseButton/Index";
import { TitleModal } from "../../../Modal/Contents/TitleModal/Index";
import { TextModal } from "../../../Modal/Contents/TextModal/Index";
import { SimpleButtonClose } from "../../../Modal/Contents/SimpleButtonClose/Index";
import { FlyToBounds, FlyToBoundsRefProps } from "../../../Leaflet/FlyToBounds/Index";
import { LeafletStaticMap } from "../../../Leaflet/LeafletStaticMap/Index";
import { OptionV2 } from "../../../FormControlsV2";
import { FooterEditOnboarding } from "../../../EditOnboarding/Footer";
import { Loading } from "../../../Loading/Index";
import {
  PastureManagementFormErrorsV2,
  PastureManagementFormFieldV2,
  PastureManagementFormV2,
  PastureManagementFormValueV2,
} from "../../../FormsV2";
import "./styles.css";

const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const frequencyOptions: OptionV2[] = [
  { label: "Semanal", value: 1, info: "WEEKLY" },
  { label: "Bisemanal", value: 2, info: "BIWEEKLY" },
  { label: "Mensal", value: 3, info: "MONTHLY" },
];

function createEmptyValue(): PastureManagementFormValueV2 {
  return {
    farm: null,
    area: null,
    microAreas: [],
    type: null,
    monitor: null,
    heightInput: 0,
    heightOutput: 0,
    occupationDays: 0,
    restDays: 0,
    rateIn: 0,
    rateOut: 0,
    range: 0,
    executionPerDay: 0,
    frequency: null,
  };
}

function normalizeCoords(coords?: any): ICoords {
  if (Array.isArray(coords)) return coords as ICoords;
  if (Array.isArray(coords?.coords)) return coords.coords;
  return [];
}

function getOptionByValue(options: OptionV2[] = [], value: any) {
  return options.find((option) => String(option.value) === String(value)) || null;
}

function toOption(value: any): OptionV2 | null {
  if (!value) return null;
  if ("label" in value && "value" in value) return value as OptionV2;
  if ("name" in value && "id" in value) return { label: value.name, value: value.id, info: value };
  return null;
}

function getMonitorOptions(type?: OptionV2 | null): OptionV2[] {
  if (!type || !Array.isArray(type.info)) return [];
  return type.info.map((monitor: any) => ({
    label: monitor.name,
    value: monitor.id,
    info: monitor,
  }));
}

function parseApiDate(value?: string) {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function getSelectedDaysFromDefault(days?: number[]) {
  const selected = new Array(weekDays.length).fill(false);
  days?.forEach((day) => {
    const index = day === 7 ? 0 : day;
    if (index >= 0 && index < selected.length) selected[index] = true;
  });
  return selected;
}

function needsSchedule(value: PastureManagementFormValueV2) {
  return !!value.type && value.type.label !== "Sem monitoramento";
}

function needsFrequency(value: PastureManagementFormValueV2) {
  return value.monitor?.label === "Altura" || value.monitor?.label === "Nota";
}

function buildDefaultFormValue(
  defaultValue: any,
  farms: OptionV2[],
  areas: OptionV2[],
  microAreas: OptionV2[],
  managementTypes: OptionV2[]
): PastureManagementFormValueV2 {
  const type = getOptionByValue(managementTypes, defaultValue?.type?.value) || toOption(defaultValue?.type);
  const monitorOptions = getMonitorOptions(type);
  const monitor = getOptionByValue(monitorOptions, defaultValue?.monitor?.value) || toOption(defaultValue?.monitor);
  const farm = getOptionByValue(farms, defaultValue?.farm?.id || defaultValue?.farm_id) || toOption(defaultValue?.farm);
  const area = getOptionByValue(areas, defaultValue?.area?.id || defaultValue?.area_id) || toOption(defaultValue?.area);
  const selectedMicroAreas = (defaultValue?.microAreas || [])
    .map((microArea: any) => getOptionByValue(microAreas, microArea?.value || microArea?.id) || toOption(microArea))
    .filter(Boolean) as OptionV2[];

  const nextValue = createEmptyValue();
  nextValue.type = type;
  nextValue.monitor = monitor;
  nextValue.farm = farm;
  nextValue.area = area;
  nextValue.microAreas = selectedMicroAreas;
  nextValue.frequency =
    frequencyOptions.find((frequency) => frequency.info === defaultValue?.frequency) || null;

  if (type?.label === "Em faixa") {
    nextValue.range = Number(defaultValue?.requirement_in || 0);
    nextValue.executionPerDay = Number(defaultValue?.execution_per_day || 0);
  } else if (monitor?.label === "Altura") {
    nextValue.heightInput = Number(defaultValue?.requirement_in || 0);
    nextValue.heightOutput = Number(defaultValue?.requirement_out || 0);
  } else if (monitor?.label === "Nota") {
    nextValue.rateIn = Number(defaultValue?.requirement_in || 0);
    nextValue.rateOut = Number(defaultValue?.requirement_out || 0);
  } else if (monitor?.label === "Dias") {
    nextValue.occupationDays = Number(defaultValue?.requirement_in || 0);
    nextValue.restDays = Number(defaultValue?.requirement_out || 0);
  }

  return nextValue;
}

export default function CreatePastureManagement() {
  const { defaultValue, setOptionForm } = useEditOnboarding();
  const { setSystemManager, pastureManagementHistory } = useFilter();
  const flytoBoundsRef = useRef<FlyToBoundsRefProps>();
  const [managementTypes, setManagementTypes] = useState<OptionV2[]>([]);
  const [monitorTypes, setMonitorTypes] = useState<OptionV2[]>([]);
  const [microAreaOptions, setMicroAreaOptions] = useState<OptionV2[]>([]);
  const [farms, setFarms] = useState<OptionV2[]>([]);
  const [areas, setAreas] = useState<OptionV2[]>([]);
  const [fitCoords, setFitCoords] = useState<ICoords>([]);
  const [currentFarm, setCurrentFarm] = useState<any>();
  const [currentArea, setCurrentArea] = useState<any>();
  const [selectedDays, setSelectedDays] = useState<boolean[]>(new Array(weekDays.length).fill(false));
  const [disabledDays, setDisabledDays] = useState<number[]>([7]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [formValue, setFormValue] = useState<PastureManagementFormValueV2>(() => createEmptyValue());
  const [errors, setErrors] = useState<PastureManagementFormErrorsV2>({});
  const [msgError, setMsgError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!defaultValue?.id);
  const [confirmInactivationVisible, setConfirmInactivationVisible] = useState(false);

  const contentStyleAlert = {
    width: "335px",
    height: "237px",
    borderRadius: "20px",
    padding: "20px",
  } as CSSProperties;

  const filteredAreas = useMemo(
    () =>
      areas
        .filter((area: any) => !formValue.farm || String(area?.info?.farm?.id) === String(formValue.farm.value))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [areas, formValue.farm]
  );

  const filteredMicroAreas = useMemo(
    () =>
      microAreaOptions
        .filter((microArea: any) => !formValue.area || String(microArea?.info?.area_id) === String(formValue.area.value))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [formValue.area, microAreaOptions]
  );

  const selectedMicroAreaInfos = useMemo(
    () => formValue.microAreas.map((microArea) => microArea.info).filter(Boolean),
    [formValue.microAreas]
  );

  function updateFormValue(nextValue: PastureManagementFormValueV2) {
    setFormValue(nextValue);
    if (Object.keys(errors).length) validate(nextValue, startDate);
  }

  function validate(value = formValue, date = startDate) {
    const validation: PastureManagementFormErrorsV2 = {};

    if (!value.farm) validation.farm = "Fazenda é necessária.";
    if (!value.area) validation.area = "Retiro é necessário.";
    if (!value.microAreas.length) validation.microAreas = "Pastos é necessário.";
    if (!value.type) validation.type = "Tipo de pastejo é necessário.";

    if (value.type && value.type.label !== "Sem monitoramento") {
      if (value.type.label !== "Em faixa" && !value.monitor) {
        validation.monitor = "Tipo de monitoramento é necessário.";
      }

      if (value.monitor?.label === "Altura") {
        if (!value.heightInput) validation.heightInput = "Altura de entrada é necessária.";
        if (!value.heightOutput) validation.heightOutput = "Altura de saída é necessária.";
      }

      if (value.monitor?.label === "Dias") {
        if (!value.occupationDays) validation.occupationDays = "Dias de ocupação é necessário.";
        if (!value.restDays) validation.restDays = "Dias de descanso é necessário.";
      }

      if (value.monitor?.label === "Nota") {
        if (!value.rateIn) validation.rateIn = "Nota do pasto é necessário.";
        if (!value.rateOut) validation.rateOut = "Nota do pasto é necessário.";
      }

      if (value.type.label === "Em faixa") {
        if (!value.range) validation.range = "Quantidade é necessário.";
        if (!value.executionPerDay) validation.executionPerDay = "Quantidade de execuções é necessário.";
      }

      if (needsFrequency(value) && !value.frequency) {
        validation.frequency = "Tipo de frequência é necessário.";
      }

      if (needsSchedule(value) && !date) {
        validation.startDate = "Selecione uma data inicial.";
      }
    }

    setErrors(validation);
    return Object.keys(validation).length === 0;
  }

  function toggleDay(index: number) {
    setSelectedDays((current) => {
      if (formValue.frequency?.label === "Bisemanal") {
        const nextSelected = new Array(weekDays.length).fill(false);
        nextSelected[index] = true;
        return nextSelected;
      }

      const nextSelected = [...current];
      nextSelected[index] = !nextSelected[index];
      return nextSelected;
    });
  }

  function handleManagementTypeChange(type: OptionV2 | null) {
    const nextMonitorTypes = getMonitorOptions(type);
    setMonitorTypes(nextMonitorTypes);
    setSelectedDays(new Array(weekDays.length).fill(false));
    setStartDate(undefined);
  }

  function handleMonitorTypeChange(monitor: OptionV2 | null) {
    if (monitor?.label === "Dias") {
      setSelectedDays(new Array(weekDays.length).fill(false));
    }
  }

  function handleFrequencyChange(frequency: OptionV2 | null) {
    if (frequency?.value === 2 || frequency?.value === 3) {
      setSelectedDays(new Array(weekDays.length).fill(false));
    }
  }

  function buildBodyPost() {
    const microAreaIds = formValue.microAreas.map((microArea) => Number(microArea.value));

    let bodyPost = {} as any;
    if (formValue.type?.label === "Sem monitoramento") {
      bodyPost = {
        micro_area_ids: microAreaIds,
        management_type_id: Number(formValue.type.value),
      };
    } else {
      let requirements = {
        requirement_in: null,
        requirement_out: null,
      } as any;

      if (formValue.monitor?.label === "Altura") {
        requirements = {
          requirement_in: formValue.heightInput,
          requirement_out: formValue.heightOutput,
        };
      } else if (formValue.monitor?.label === "Nota") {
        requirements = {
          requirement_in: formValue.rateIn,
          requirement_out: formValue.rateOut,
        };
      } else if (formValue.monitor?.label === "Dias") {
        requirements = {
          requirement_in: formValue.occupationDays,
          requirement_out: formValue.restDays,
        };
      } else if (formValue.type?.label === "Em faixa") {
        requirements = {
          requirement_in: formValue.range,
          requirement_out: null,
        };
      }

      bodyPost = {
        micro_area_ids: microAreaIds,
        management_type_id: Number(formValue.type?.value),
        monitoring_type_id: Number(formValue.monitor?.value),
        ...requirements,
        frequency: formValue.frequency?.info,
        days_of_week: selectedDays
          .map((day, index) => (day ? (index === 0 ? 7 : index) : -1))
          .filter((day) => day !== -1),
        execution_per_day: formValue.executionPerDay || null,
        start_date: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
      };

      if (defaultValue?.id) {
        bodyPost.management_group_id = defaultValue.id;
      }
    }

    return bodyPost;
  }

  function postManagement(bodyPost: any) {
    setSubmitting(true);
    axios
      .post("/micro-area-management", bodyPost)
      .then(() => {
        setOptionForm(undefined);
        setSystemManager(null);
      })
      .catch((err) => setMsgError(err.response?.data?.msg || "Erro ao criar manejo de pasto"))
      .finally(() => setSubmitting(false));
  }

  function submitForm() {
    if (submitting || pastureManagementHistory) return;
    if (!validate()) return;

    postManagement(buildBodyPost());
  }

  function deleteManagement() {
    if (submitting || pastureManagementHistory || !defaultValue?.id) return;

    setConfirmInactivationVisible(false);
    postManagement({
      ...buildBodyPost(),
      management_type_id: 1,
    });
  }

  useEffect(() => {
    let active = true;
    setLoadingEdit(!!defaultValue?.id);

    Promise.all([
      axios.get("micro-area-management/types"),
      axios.get("/farms/user"),
      axios.get("/areas/user"),
      axios.get("/micro-areas"),
    ])
      .then(([managementTypesResponse, farmsResponse, areasResponse, microAreasResponse]) => {
        if (!active) return;

        const nextManagementTypes = managementTypesResponse.data.map((type: any) => ({
          label: type.name,
          value: type.id,
          info: type.monitoring_types,
        }));
        const nextFarms = farmsResponse.data.map((farm: any) => ({
          label: farm.name,
          value: farm.id,
          info: farm,
        }));
        const nextAreas = areasResponse.data.map((area: any) => ({
          label: area.name,
          value: area.id,
          info: area,
        }));
        const nextMicroAreas = microAreasResponse.data.map((microArea: any) => ({
          label: `${microArea.farm.initials}-${microArea.area.initials}-${microArea.initials}`,
          value: microArea.id,
          info: microArea,
        }));

        setManagementTypes(nextManagementTypes);
        setFarms(nextFarms);
        setAreas(nextAreas);
        setMicroAreaOptions(nextMicroAreas);

        if (defaultValue) {
          const nextValue = buildDefaultFormValue(
            defaultValue,
            nextFarms,
            nextAreas,
            nextMicroAreas,
            nextManagementTypes
          );
          setFormValue(nextValue);
          setMonitorTypes(getMonitorOptions(nextValue.type));
          setCurrentFarm(nextValue.farm?.info || defaultValue.farm);
          setCurrentArea(nextValue.area?.info || defaultValue.area);
          setSelectedDays(getSelectedDaysFromDefault(defaultValue.days_of_week));
          setStartDate(parseApiDate(defaultValue.start_date));
        }
      })
      .catch((err) => {
        if (active) setMsgError(err.response?.data?.msg || "Erro ao carregar manejo de pasto");
      })
      .finally(() => {
        if (active) setLoadingEdit(false);
      });

    return () => {
      active = false;
    };
  }, [defaultValue]);

  useEffect(() => {
    if (currentFarm?.map_coords) {
      setFitCoords(normalizeCoords(currentFarm.map_coords));
    }
  }, [currentFarm]);

  useEffect(() => {
    if (currentArea?.map_coords) {
      setFitCoords(normalizeCoords(currentArea.map_coords));
    }
  }, [currentArea]);

  useEffect(() => {
    if (formValue.frequency?.value === 3 || formValue.monitor?.label === "Dias" || formValue.type?.label === "Em faixa") {
      setDisabledDays([7]);
      return;
    }

    setDisabledDays(selectedDays.map((day, index) => (!day ? index : 7)));
    if (startDate && !selectedDays[startDate.getDay()]) {
      setStartDate(undefined);
    }
  }, [formValue.frequency, formValue.monitor, formValue.type, selectedDays, startDate]);

  const mapSlot = (
    <div className="pasture-management-v2__map-column">
      <div className="area__drawarea--container edit-onboarding-v2__map-preview">
        <LeafletStaticMap>
          <FlyToBounds ref={flytoBoundsRef} fitCoords={fitCoords}>
            {fitCoords.length > 0 && <Polygon positions={fitCoords} color={Constants.LEAFLET_UNFOCUS.color} />}
            {selectedMicroAreaInfos.map((micro: any) => (
              <Polygon
                key={micro.id || micro.uuid}
                positions={micro.map_coords || []}
                color={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.color}
                fillColor={Constants.LEAFLET_NORMAL_BLUE.fillColor}
                fillOpacity={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.fillOpacity}
              />
            ))}
          </FlyToBounds>
        </LeafletStaticMap>
      </div>
    </div>
  );

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError("")} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>Algo deu errado.</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError("")}>Voltar</ButtonRedirect>
        </section>
      </AlertModal>

      <Modal
        visible={confirmInactivationVisible}
        contentStyle={{
          width: "600px",
          height: "fit-content",
          borderRadius: 20,
          padding: "40px",
          position: "relative",
        }}
      >
        <div style={{ position: "absolute", top: 20, right: 20 }}>
          <RoundedCloseButton onClick={() => setConfirmInactivationVisible(false)} />
        </div>
        <section style={{ display: "flex", flexDirection: "column", gap: 30 }}>
          <header>
            <TitleModal>Deseja inativar este Manejo de Pasto?</TitleModal>
          </header>
          <footer style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
            <ButtonOnlyTitle
              title="Cancelar"
              theme="light"
              type="button"
              onClick={() => setConfirmInactivationVisible(false)}
            />
            <ButtonOnlyTitle
              title="Confirmar"
              theme="info"
              type="button"
              disabled={submitting}
              onClick={deleteManagement}
            />
          </footer>
        </section>
      </Modal>

      <main className="body__formEditOnboarding body__formEditOnboarding--property-v2">
        <Loading visible={loadingEdit} contained />
        <PastureManagementFormV2
          value={formValue}
          errors={errors}
          farms={farms.sort((a, b) => a.label.localeCompare(b.label))}
          areas={filteredAreas}
          microAreas={filteredMicroAreas}
          managementTypes={managementTypes}
          monitorTypes={monitorTypes}
          frequencyOptions={frequencyOptions}
          selectedDays={selectedDays}
          weekDays={weekDays}
          disabledDays={disabledDays}
          startDate={startDate}
          endDate={parseApiDate(defaultValue?.end_date)}
          historyMode={pastureManagementHistory}
          mapSlot={mapSlot}
          onChange={updateFormValue}
          onFarmChange={(farm) => {
            setCurrentFarm(farm?.info);
            setCurrentArea(undefined);
            setFitCoords(normalizeCoords(farm?.info?.map_coords));
          }}
          onAreaChange={(area) => {
            setCurrentArea(area?.info);
            setFitCoords(normalizeCoords(area?.info?.map_coords));
          }}
          onMicroAreasChange={() => undefined}
          onManagementTypeChange={handleManagementTypeChange}
          onMonitorTypeChange={handleMonitorTypeChange}
          onFrequencyChange={handleFrequencyChange}
          onDayToggle={toggleDay}
          onStartDateChange={(date) => {
            setStartDate(date);
            if (errors.startDate) validate(formValue, date);
          }}
        />
      </main>

      {!pastureManagementHistory && (
        <FooterEditOnboarding
          undoFunction={() => setOptionForm(undefined)}
          saveFunction={submitForm}
          deleteFunction={defaultValue?.id ? () => setConfirmInactivationVisible(true) : undefined}
          isNew={!defaultValue?.id}
          disabledSubmit={submitting}
        />
      )}
    </>
  );
}
